import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { EpisodeManifest, Extra } from "@scrile/contracts";
import { describeError, isApiError } from "../api/errors";
import { useApi } from "../api/context";
import { t } from "../i18n";
import { trackOnce } from "../lib/analytics";
import { episodeState, followingEpisode, nextPlayable } from "../lib/episodes";
import {
  episodeKey,
  extraKey,
  loadPosition,
  loadRate,
  nextRate,
  resumePoint,
  savePosition,
  saveRate,
} from "../lib/positions";
import { sleepTimerStatus, startSleepTimer, type SleepSetting } from "../lib/sleepTimer";
import { toneWav, wavObjectUrl } from "../lib/wav";
import { useHeroData } from "../state/heroData";
import { useToast } from "../state/toast";

/**
 * Плеер живёт над экранами: один <audio> на всё приложение. Уйти со «Плеера» на
 * страницу героя или в аккаунт — не значит остановить серию; внизу остаётся мини-плеер,
 * а на заблокированном телефоне — кнопки на экране блокировки (Media Session).
 *
 * Состояние и действия — два контекста: время обновляется четыре раза в секунду, и
 * кнопке «Start the pilot» незачем перерисовываться вместе с ним.
 */

export type Track =
  | {
      kind: "episode";
      key: string;
      ordinal: number;
      title: string;
      audioUrl: string;
      free: boolean;
      manifest: EpisodeManifest;
    }
  | { kind: "extra"; key: string; id: string; title: string; audioUrl: string; extra: Extra }
  | { kind: "trailer"; key: string; title: string; audioUrl: string };

export type PlayerStatus = "idle" | "loading" | "playing" | "paused" | "ended" | "error";

export type Requested = { kind: "episode"; ordinal: number } | { kind: "other" } | null;

export interface PlayerState {
  track: Track | null;
  /** Что попросили играть — может ещё грузиться. По нему «Плеер» сверяет адрес. */
  requested: Requested;
  status: PlayerStatus;
  buffering: boolean;
  currentTime: number;
  duration: number;
  rate: number;
  sleep: SleepSetting;
  sleepRemainingMs: number | null;
  error: string | null;
  /** Почему закончилось: дальше пейвол или сезон пока весь прослушан. */
  endedReason: "paywall" | "caught_up" | null;
  /** Растёт при каждой записи позиции: списки перечитывают прогресс. */
  positionsVersion: number;
}

export type SleepOption = { mode: "off" } | { mode: "minutes"; minutes: number } | { mode: "episode_end" };

export interface PlayerActions {
  /** Вызывать из обработчика нажатия: на iPhone звук разрешён только в жесте. */
  playEpisode(ordinal: number, options?: { autoplay?: boolean }): Promise<void>;
  playExtra(extra: Extra): void;
  playTrailer(url: string, title: string): void;
  toggle(): void;
  seek(seconds: number): void;
  skip(deltaSeconds: number): void;
  cycleRate(): void;
  setSleep(option: SleepOption): void;
  stop(): void;
  /** Серия, которую играем или грузим, — синхронно, без ожидания перерисовки. */
  requestedOrdinal(): number | null;
}

const INITIAL: PlayerState = {
  track: null,
  requested: null,
  status: "idle",
  buffering: false,
  currentTime: 0,
  duration: 0,
  rate: 1,
  sleep: { mode: "off" },
  sleepRemainingMs: null,
  error: null,
  endedReason: null,
  positionsVersion: 0,
};

const StateContext = createContext<PlayerState | null>(null);
const ActionsContext = createContext<PlayerActions | null>(null);

/** Как часто писать позицию во время игры: чаще — нагрузка, реже — теряем минуту при закрытии. */
const SAVE_EVERY_MS = 5_000;

let silentUrl: string | null = null;
/** Десятая секунды тишины: ею «разблокируется» звук на iPhone, пока грузится манифест. */
function silence(): string {
  silentUrl ??= wavObjectUrl(toneWav({ seconds: 0.1, volume: 0, sampleRate: 8_000 }));
  return silentUrl;
}

const finite = (value: number) => (Number.isFinite(value) && value > 0 ? value : 0);

/**
 * Куда вести за подпиской. reason: "locked" — человек сам нажал на закрытую серию;
 * "ended" — дослушал, а следующая закрыта (решает App: уводить ли с текущего экрана).
 */
export type PaywallHandler = (ordinal: number, reason: "locked" | "ended") => void;

export function PlayerProvider({ children, onPaywall }: { children: ReactNode; onPaywall: PaywallHandler }) {
  const api = useApi();
  const toast = useToast();
  const { hero, episodes, me } = useHeroData();
  const audioRef = useRef<HTMLAudioElement>(null);
  const [state, setState] = useState<PlayerState>(() => ({ ...INITIAL, rate: loadRate() }));

  // Свежие значения для обработчиков событий <audio>, которые вешаются один раз
  const live = useRef({ api, toast, hero, episodes, me, onPaywall });
  live.current = { api, toast, hero, episodes, me, onPaywall };

  const controller = useMemo(() => {
    const refs = {
      track: null as Track | null,
      requested: null as Requested,
      sleep: { mode: "off" } as SleepSetting,
      rate: loadRate(),
      loadSeq: 0,
      resumePending: false,
      savedAt: 0,
      startedKey: null as string | null,
      unlocked: false,
      /** Кому и какие серии уже закреплены (POST /start): гость начал пилот, вошёл — закрепляем ещё раз. */
      pinned: new Set<string>(),
    };

    const audio = () => audioRef.current;
    const patch = (next: Partial<PlayerState>) => setState((current) => ({ ...current, ...next }));
    const isSilent = (element: HTMLAudioElement) => silentUrl !== null && element.currentSrc === silentUrl;

    function savePositionNow(finished: boolean): void {
      const element = audio();
      const track = refs.track;
      if (!element || !track || track.kind === "trailer") return;
      const duration = finite(element.duration);
      if (!duration) return;
      savePosition(track.key, {
        positionSec: finished ? duration : element.currentTime,
        durationSec: duration,
        finished,
        updatedAt: Date.now(),
      });
      refs.savedAt = Date.now();
      setState((current) => ({ ...current, positionsVersion: current.positionsVersion + 1 }));
    }

    function mediaSession(track: Track): void {
      if (typeof navigator === "undefined" || !("mediaSession" in navigator)) return;
      const { hero: currentHero } = live.current;
      const art = currentHero.theme.portraitUrl ?? currentHero.theme.coverUrl;
      try {
        navigator.mediaSession.metadata = new MediaMetadata({
          title: track.title,
          artist: currentHero.name,
          album: track.kind === "episode" ? t("episode.number", { n: track.ordinal }) : "",
          artwork: art ? [{ src: new URL(art, window.location.href).href, sizes: "512x512" }] : [],
        });
      } catch {
        // Старый Safari без MediaMetadata: кнопки экрана блокировки просто без обложки
      }
    }

    function positionState(): void {
      const element = audio();
      if (!element || typeof navigator === "undefined" || !("mediaSession" in navigator)) return;
      const duration = finite(element.duration);
      if (!duration) return;
      try {
        navigator.mediaSession.setPositionState({
          duration,
          playbackRate: element.playbackRate || 1,
          position: Math.min(element.currentTime, duration),
        });
      } catch {
        // setPositionState есть не везде; это украшение, не функция
      }
    }

    function applySleep(): void {
      const setting = refs.sleep;
      const element = audio();
      if (setting.mode !== "timer") return;
      const status = sleepTimerStatus(setting.endsAt, Date.now(), setting.fadeMs);
      // На iPhone громкость <audio> только для чтения: там таймер просто останавливает звук
      if (element) element.volume = status.volume;
      if (status.done) {
        element?.pause();
        if (element) element.volume = 1;
        refs.sleep = { mode: "off" };
        patch({ sleep: refs.sleep, sleepRemainingMs: null });
        live.current.toast.show(t("sleep.ended"), 8_000);
        return;
      }
      setState((current) =>
        current.sleepRemainingMs !== null &&
        Math.ceil(current.sleepRemainingMs / 1000) === Math.ceil(status.remainingMs / 1000)
          ? current
          : { ...current, sleepRemainingMs: status.remainingMs },
      );
    }

    /**
     * iPhone разрешает звук, только если первый play() элемента случился в жесте. Манифест
     * серии грузится после нажатия, поэтому в самом жесте элемент «запускается» впустую:
     * тишиной, а если в нём уже стоит незапущенная дорожка (зашли по прямой ссылке) — ею
     * же, но без звука и сразу на паузу. Дальше play() после await проходит.
     */
    function unlock(): void {
      const element = audio();
      if (!element || refs.unlocked) return;
      refs.unlocked = true;
      if (!element.getAttribute("src")) {
        element.src = silence();
        element.play().catch(() => undefined);
        return;
      }
      if (!element.paused) return;
      const loaded = element.src;
      element.muted = true;
      element
        .play()
        // Манифест мог прийти раньше: новую дорожку не трогаем
        .then(() => element.src === loaded && element.pause())
        .catch(() => undefined)
        .finally(() => {
          element.muted = false;
        });
    }

    /** Громкость с учётом таймера сна: новая дорожка посреди затухания не должна грянуть в полную силу. */
    function currentVolume(): number {
      const setting = refs.sleep;
      return setting.mode === "timer" ? sleepTimerStatus(setting.endsAt, Date.now(), setting.fadeMs).volume : 1;
    }

    function onPlayRejected(error: unknown): void {
      // AbortError — дорожку сменили, пока запускалась: не ошибка
      if (error instanceof DOMException && error.name === "AbortError") return;
      patch({ status: "paused", buffering: false });
    }

    function switchTo(track: Track, autoplay: boolean): void {
      const element = audio();
      if (!element) return;
      const previous = refs.track;
      if (previous && previous.key !== track.key && !element.ended) savePositionNow(false);
      refs.track = track;
      refs.startedKey = null;
      refs.resumePending = track.kind !== "trailer";
      element.src = track.audioUrl;
      // Safari сбрасывает скорость при смене src
      element.defaultPlaybackRate = refs.rate;
      element.playbackRate = refs.rate;
      element.volume = currentVolume();
      setState((current) => ({
        ...current,
        track,
        requested: refs.requested,
        status: autoplay ? "loading" : "paused",
        buffering: autoplay,
        currentTime: 0,
        duration: track.kind === "episode" ? track.manifest.lengthSec : 0,
        error: null,
        endedReason: null,
      }));
      mediaSession(track);
      if (autoplay) element.play().catch(onPlayRejected);
    }

    async function playEpisode(ordinal: number, options: { autoplay?: boolean } = {}): Promise<void> {
      const autoplay = options.autoplay ?? true;
      if (autoplay) unlock();
      refs.requested = { kind: "episode", ordinal };
      const seq = ++refs.loadSeq;
      const current = refs.track;
      const element = audio();
      if (current?.kind === "episode" && current.ordinal === ordinal && element && !element.error) {
        patch({ requested: refs.requested });
        if (autoplay && element.paused) element.play().catch(onPlayRejected);
        return;
      }
      patch({ requested: refs.requested, status: "loading", error: null, endedReason: null });
      try {
        const manifest = await live.current.api.episode(ordinal);
        if (seq !== refs.loadSeq) return; // пока грузили, выбрали другую
        const summary = live.current.episodes.find((episode) => episode.ordinal === ordinal);
        switchTo(
          {
            kind: "episode",
            key: episodeKey(live.current.hero.slug, ordinal),
            ordinal,
            title: manifest.title,
            audioUrl: manifest.audioUrl,
            free: summary?.free ?? false,
            manifest,
          },
          autoplay,
        );
      } catch (error) {
        if (seq !== refs.loadSeq) return;
        const playing = refs.track;
        refs.requested = playing?.kind === "episode" ? { kind: "episode", ordinal: playing.ordinal } : null;
        if (isApiError(error, "payment_required")) {
          patch({ requested: refs.requested, status: playing ? "paused" : "idle" });
          live.current.onPaywall(ordinal, "locked");
          return;
        }
        patch({ requested: refs.requested, status: "error", error: describeError(error, "episode") });
      }
    }

    function playOther(track: Track): void {
      const element = audio();
      if (refs.track?.key === track.key && element) {
        if (element.paused) element.play().catch(onPlayRejected);
        else element.pause();
        return;
      }
      unlock();
      refs.requested = { kind: "other" };
      refs.loadSeq += 1; // отменить недогруженную серию
      switchTo(track, true);
    }

    function markStarted(): void {
      const track = refs.track;
      if (!track || track.kind !== "episode") return;
      // Ревизия закрепляется за аккаунтом — гостю закреплять нечего
      const me = live.current.me;
      if (me && !refs.pinned.has(`${me.id}:${track.key}`)) {
        refs.pinned.add(`${me.id}:${track.key}`);
        void live.current.api.startEpisode(track.ordinal).catch(() => {
          // Аудио уже загружено; не мешаем слушать, но следующая попытка закрепит ревизию.
          refs.pinned.delete(`${me.id}:${track.key}`);
        });
      }
      if (refs.startedKey === track.key) return;
      refs.startedKey = track.key;
      // Ключ с моментом загрузки дорожки: повторное прослушивание — новое событие, двойной эффект — нет
      const once = `${track.key}:${refs.loadSeq}`;
      if (track.free) trackOnce(`pilot:${once}`, "pilot_started", { episode: track.ordinal });
      trackOnce(`start:${once}`, "episode_started", { episode: track.ordinal });
    }

    function handleEnded(): void {
      const track = refs.track;
      if (!track) return;
      savePositionNow(true);
      if (track.kind === "episode") {
        const once = `${track.key}:${refs.loadSeq}`;
        trackOnce(`finish:${once}`, "episode_finished", { episode: track.ordinal });
        if (track.free) trackOnce(`pilotEnd:${once}`, "pilot_finished", { episode: track.ordinal });
      }
      if (refs.sleep.mode === "episode_end") {
        refs.sleep = { mode: "off" };
        patch({ status: "ended", sleep: refs.sleep, endedReason: null, buffering: false });
        live.current.toast.show(t("sleep.ended"), 8_000);
        return;
      }
      if (track.kind !== "episode") {
        patch({ status: "ended", buffering: false });
        return;
      }
      const { episodes: list, onPaywall: showPaywall } = live.current;
      const next = nextPlayable(list, track.ordinal);
      if (next) {
        void playEpisode(next.ordinal, { autoplay: true });
        return;
      }
      const following = followingEpisode(list, track.ordinal);
      if (following && episodeState(following) === "locked") {
        patch({ status: "ended", endedReason: "paywall", buffering: false });
        showPaywall(following.ordinal, "ended");
        return;
      }
      patch({ status: "ended", endedReason: "caught_up", buffering: false });
    }

    const actions: PlayerActions = {
      playEpisode,
      playExtra(extra) {
        playOther({
          kind: "extra",
          key: extraKey(live.current.hero.slug, extra.id),
          id: extra.id,
          title: extra.title,
          audioUrl: extra.audioUrl,
          extra,
        });
      },
      playTrailer(url, title) {
        playOther({ kind: "trailer", key: `${live.current.hero.slug}:trailer`, title, audioUrl: url });
      },
      toggle() {
        const element = audio();
        if (!element || !refs.track) return;
        if (element.paused) element.play().catch(onPlayRejected);
        else element.pause();
      },
      seek(seconds) {
        const element = audio();
        if (!element || !refs.track) return;
        const duration = finite(element.duration);
        const target = Math.max(0, duration ? Math.min(seconds, duration - 0.05) : seconds);
        element.currentTime = target;
        patch({ currentTime: target });
        savePositionNow(false);
      },
      skip(deltaSeconds) {
        const element = audio();
        if (!element) return;
        actions.seek(element.currentTime + deltaSeconds);
      },
      cycleRate() {
        const rate = nextRate(refs.rate);
        refs.rate = rate;
        const element = audio();
        if (element) {
          element.defaultPlaybackRate = rate;
          element.playbackRate = rate;
        }
        saveRate(rate);
        patch({ rate });
      },
      setSleep(option) {
        const element = audio();
        if (element) element.volume = 1;
        if (option.mode === "minutes") {
          refs.sleep = startSleepTimer(option.minutes, Date.now());
          patch({ sleep: refs.sleep, sleepRemainingMs: option.minutes * 60_000 });
        } else {
          refs.sleep = option.mode === "episode_end" ? { mode: "episode_end" } : { mode: "off" };
          patch({ sleep: refs.sleep, sleepRemainingMs: null });
        }
      },
      stop() {
        const element = audio();
        if (element) {
          if (!element.ended) savePositionNow(false);
          element.pause();
          element.removeAttribute("src");
          element.load();
        }
        refs.track = null;
        refs.requested = null;
        refs.loadSeq += 1;
        setState((current) => ({ ...INITIAL, rate: current.rate, positionsVersion: current.positionsVersion }));
      },
      requestedOrdinal() {
        return refs.requested?.kind === "episode" ? refs.requested.ordinal : null;
      },
    };

    const listeners: Record<string, (element: HTMLAudioElement) => void> = {
      loadedmetadata(element) {
        if (isSilent(element)) return;
        if (refs.resumePending && refs.track) {
          refs.resumePending = false;
          const at = resumePoint(loadPosition(refs.track.key), element.duration);
          if (at > 0) element.currentTime = at;
        }
        element.playbackRate = refs.rate;
        patch({ duration: finite(element.duration), currentTime: element.currentTime });
        positionState();
      },
      durationchange(element) {
        if (!isSilent(element)) patch({ duration: finite(element.duration) });
      },
      timeupdate(element) {
        if (isSilent(element)) return;
        patch({ currentTime: element.currentTime });
        if (!element.paused && Date.now() - refs.savedAt > SAVE_EVERY_MS) savePositionNow(false);
        applySleep();
      },
      play(element) {
        // Любой состоявшийся play() — элемент разблокирован и для последующих без жеста
        refs.unlocked = true;
        if (isSilent(element) || element.muted) return;
        patch({ status: "playing", error: null, endedReason: null });
        markStarted();
      },
      playing(element) {
        if (!isSilent(element)) patch({ status: "playing", buffering: false });
      },
      waiting(element) {
        if (!isSilent(element)) patch({ buffering: true });
      },
      pause(element) {
        if (isSilent(element) || element.ended || element.muted) return;
        patch({ status: "paused", buffering: false });
        savePositionNow(false);
      },
      seeked(element) {
        if (!isSilent(element)) positionState();
      },
      ended(element) {
        if (!isSilent(element)) handleEnded();
      },
      error(element) {
        if (isSilent(element) || !refs.track || !element.getAttribute("src")) return;
        patch({ status: "error", error: t("error.audio"), buffering: false });
      },
    };

    return { actions, listeners, applySleep, savePositionNow };
  }, []);

  // События <audio> — один раз на жизнь элемента
  useEffect(() => {
    const element = audioRef.current;
    if (!element) return;
    const bound = Object.entries(controller.listeners).map(([name, handler]) => {
      const listener = () => handler(element);
      element.addEventListener(name, listener);
      return [name, listener] as const;
    });
    return () => bound.forEach(([name, listener]) => element.removeEventListener(name, listener));
  }, [controller]);

  // Кнопки экрана блокировки и наушников
  useEffect(() => {
    if (typeof navigator === "undefined" || !("mediaSession" in navigator)) return;
    const { actions } = controller;
    const handlers: [MediaSessionAction, MediaSessionActionHandler][] = [
      ["play", () => actions.toggle()],
      ["pause", () => actions.toggle()],
      ["seekbackward", () => actions.skip(-15)],
      ["seekforward", () => actions.skip(15)],
      ["seekto", (details) => details.seekTime !== undefined && actions.seek(details.seekTime)],
    ];
    for (const [action, handler] of handlers) {
      try {
        navigator.mediaSession.setActionHandler(action, handler);
      } catch {
        // Действие не поддерживается этим браузером
      }
    }
    return () => {
      for (const [action] of handlers) {
        try {
          navigator.mediaSession.setActionHandler(action, null);
        } catch {
          // см. выше
        }
      }
    };
  }, [controller]);

  // Таймер сна тикает и без timeupdate: на паузе и когда браузер реже шлёт события
  useEffect(() => {
    if (state.sleep.mode !== "timer") return;
    const id = window.setInterval(() => controller.applySleep(), 1_000);
    return () => window.clearInterval(id);
  }, [state.sleep, controller]);

  // Закрыли вкладку или свернули приложение — позиция должна пережить это
  useEffect(() => {
    const save = () => {
      const element = audioRef.current;
      if (element && !element.paused) controller.savePositionNow(false);
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") save();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", save);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", save);
    };
  }, [controller]);

  return (
    <ActionsContext.Provider value={controller.actions}>
      <StateContext.Provider value={state}>
        {children}
        <audio ref={audioRef} preload="metadata" className="sr-only" aria-hidden="true" />
      </StateContext.Provider>
    </ActionsContext.Provider>
  );
}

export function usePlayer(): PlayerState {
  const state = useContext(StateContext);
  if (!state) throw new Error("usePlayer outside of PlayerProvider");
  return state;
}

export function usePlayerActions(): PlayerActions {
  const actions = useContext(ActionsContext);
  if (!actions) throw new Error("usePlayerActions outside of PlayerProvider");
  return actions;
}
